import { useMemo } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';

import { listCustomerProfiles } from '../api/endpoints/customerFiles';
import type { CustomerFile, CustomerNote, CustomerProfile } from '../api/schemas';
import { usePermissions } from '../auth/usePermissions';
import { profileIndex } from './customerFile';
import { minPlanFor } from '@dale/plans';

import { meetsPlan } from './plan';
import { useBusiness } from './useBusiness';

export const CUSTOMER_PROFILES_KEY = ['customer-profiles'] as const;
const PROFILES_STALE_MS = 30_000;

export function customerFileKey(customerId: string) {
  return ['customer-file', customerId] as const;
}

/**
 * Every annotated client of this business, indexed by customer id. Rosters and
 * lists read levels from here, so one request serves a whole class. Quietly
 * empty below the Base plan, where the API answers plan_required, and for a
 * profile without Clientes (a waiter's, an accountant's), which it refuses.
 */
export function useCustomerProfiles(): Map<string, CustomerProfile> {
  const business = useBusiness();
  const { can } = usePermissions();
  const query = useQuery({
    queryKey: CUSTOMER_PROFILES_KEY,
    queryFn: listCustomerProfiles,
    enabled: Boolean(business) && meetsPlan(business?.plan, minPlanFor('clientes')) && can('clientes'),
    staleTime: PROFILES_STALE_MS,
    retry: false,
  });
  return useMemo(() => profileIndex(query.data ?? []), [query.data]);
}

/** Nothing the API would list: no level, tag, note, or glance field (injuries, music, favorite coach, pause). */
function isEmptyProfile(entry: CustomerProfile): boolean {
  return (
    entry.level === null &&
    entry.tags.length === 0 &&
    entry.noteCount === 0 &&
    entry.injuries === null &&
    entry.music === null &&
    entry.favoriteStaffId === null &&
    entry.pause === null
  );
}

/** Writes one saved profile into both caches without refetching the roster. */
export function storeProfile(queryClient: QueryClient, saved: CustomerProfile): void {
  queryClient.setQueryData<CustomerProfile[]>(CUSTOMER_PROFILES_KEY, (current) => {
    if (!current) return current;
    const others = current.filter((entry) => entry.customerId !== saved.customerId);
    return isEmptyProfile(saved) ? others : [...others, saved];
  });
  queryClient.setQueryData<CustomerFile>(customerFileKey(saved.customerId), (current) =>
    current ? { ...current, profile: saved } : current,
  );
}

/** A note landed or left: the file's list and the profile's count move together. */
export function storeNotes(
  queryClient: QueryClient,
  customerId: string,
  notes: CustomerNote[],
  countDelta: 1 | -1,
): void {
  const current = queryClient.getQueryData<CustomerFile>(customerFileKey(customerId));
  if (!current) return;
  const profile: CustomerProfile = {
    ...current.profile,
    // The file carries the newest hundred notes; the count is the server's.
    noteCount: Math.max(current.profile.noteCount + countDelta, 0),
    lastNoteAt: notes[0]?.createdAt ?? null,
  };
  queryClient.setQueryData<CustomerFile>(customerFileKey(customerId), { profile, notes });
  storeProfile(queryClient, profile);
}
